import {
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'

import * as React from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { allCountries } from 'country-telephone-data'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import Flag from 'LuxKit/Flag'
import { getPrefixCountry } from 'lib/geo/getPrefixCountry'
import noop from 'lib/function/noop'
import PhonePrefixItem from './PhonePrefixItem'
import TextInputOnly from '../TextInputOnly'
import InputLabelWrap from '../InputLabelWrap'
import config from 'constants/config'
import useToggle from 'hooks/useToggle'
import BodyText from 'LuxKit/Typography/BodyText'
import Clickable from 'components/Common/Clickable'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import ModalContext from 'contexts/ModalContext'
import InputText from 'LuxKit/Typography/InputText'
import HiddenInput from '../HiddenInput'
import PhoneInputPrefixModal from './PhoneInputPrefixModal'
import TextInput from '../TextInput'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { sortBy } from 'lib/array/arrayUtils'
import { ISO2_START_ORDER } from 'constants/config/region'
import focusStyle from 'styles/utils/focusStyle'
import { useIntl } from 'react-intl'

const PhoneNumberInput = styled(TextInputOnly)`
  && > input {
    padding-left: ${rem(104)};
  }
`

const PrefixDropdown = styled(Clickable)`
  display: flex;
  align-items: center;
  gap: ${rem(4)};

  &:focus-visible {
    ${focusStyle}
  }
`

export interface PhonePrefixCountry {
  dialCode: string
  format: string
  iso2: string
  name: string
  priority: number
}

interface Props {
  defaultPhoneNumber?: string
  defaultPhonePrefix?: string
  countryCodeNameOverride?: string
  phoneNumber?: string
  onNumberChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onPrefixChange?: (data: { phonePrefix: string, code: string }) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
  prefixCountry?: string
  phonePrefix?: string
  placeholder?: string
  inputRef?: any
  name?: string
  required?: boolean
  requiredErrorMessage?: string
  inputTestId?: string
  id?: string
  noValidationSpacing?: boolean
  label?: string
  className?: string
  autoComplete?: string
  phonePrefixOptions?: Array<PhonePrefixCountry>
  disabled?: boolean
  allowLandline?: boolean
  /**
   * When true, uses libphonenumber-js for full phone number validation
   * (format, number type, country-specific rules). When false (default),
   * only AU numbers get basic format validation.
   */
  useStrictValidation?: boolean
  /**
   * By default the name will assume a nested object.
   * In VERY RARE circumstances you might need to change this, this prop allows
   * changing the prefix name to a flat string
   */
  prefixNameOverride?: string
  /**
   * By default the name will assume a nested object.
   * In VERY RARE circumstances you might need to change this, this prop allows
   * changing the phone name to a flat string
   */
  phoneNameOverride?: string
  helpText?: string
  hidden?: boolean
}

function PhoneInput(props: Props) {
  const {
    autoComplete = 'tel-national',
    defaultPhoneNumber,
    defaultPhonePrefix,
    phoneNumber,
    onBlur = noop,
    onFocus = noop,
    prefixCountry,
    phonePrefix,
    placeholder: placeholderProp,
    inputRef,
    required,
    requiredErrorMessage,
    name,
    onNumberChange = noop,
    inputTestId,
    id: propsId,
    onPrefixChange = noop,
    label,
    noValidationSpacing,
    className,
    phonePrefixOptions,
    disabled,
    prefixNameOverride,
    phoneNameOverride,
    countryCodeNameOverride,
    helpText,
    hidden,
    allowLandline = false,
    useStrictValidation = false,
  } = props

  const intl = useIntl()
  const placeholder = placeholderProp ?? intl.formatMessage({
    id: '_nKnm24VECv-D1XqM2Hj',
    defaultMessage: 'Phone',
    description: 'Default placeholder for phone number input when field is empty',
  })

  const prefixTriggerRef = useRef<HTMLButtonElement>(null)
  const prefixSearchRef = useRef<HTMLInputElement>(null)
  const [isPrefixDropdownOpen, togglePrefixDropdown, , closePrefixDropdown] = useToggle()
  const [localPrefix, setLocalPrefix] = useState<string | undefined>(phonePrefix ?? defaultPhonePrefix)
  const [localCountryCode, setLocalCountryCode] = useState<string | undefined>(prefixCountry)
  const [prefixTypeahead, setPrefixTypeahead] = useState<string | undefined>()
  const [localNumber, setLocalNumber] = useState<string | undefined>(phoneNumber ?? defaultPhoneNumber ?? '')

  const formId = useId()
  const id = propsId ?? formId

  const isMobile = useScreenSizeOnly('mobile')
  const showModal = useContext(ModalContext)

  useEffect(() => {
    if (useStrictValidation) {
      import('./phoneValidationRules')
    }
  }, [useStrictValidation])

  const onPrefixSelect = useCallback((country: PhonePrefixCountry) => {
    setLocalPrefix(country.dialCode)
    setLocalCountryCode(country.iso2.toUpperCase())
    setPrefixTypeahead(undefined)
    setLocalNumber('')
    onPrefixChange({
      phonePrefix: country.dialCode,
      code: country.iso2.toUpperCase(),
    })
    closePrefixDropdown()
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [onPrefixChange])

  const allowedCountries = useMemo<Array<PhonePrefixCountry>>(() => {
    if (phonePrefixOptions) {
      return phonePrefixOptions
    }
    if (config.restrictPhoneNumbersToSupportedRegions) {
      const allowedCodes = new Set(config.regions.map(region => region.code))
      return allCountries.filter((country: PhonePrefixCountry) => (allowedCodes.has(country.iso2.toUpperCase())))
    }
    return allCountries
  }, [phonePrefixOptions])

  const onPrefixToggle = useCallback(async() => {
    if (isMobile) {
      const nextCountry = await showModal<PhonePrefixCountry | undefined>(<PhoneInputPrefixModal
        countries={allowedCountries}
        selectedCountryCode={localCountryCode || prefixCountry}
      />,
      )
      if (nextCountry) {
        onPrefixSelect(nextCountry)
      }
    } else {
      togglePrefixDropdown()
    }
  }, [allowedCountries, isMobile, localCountryCode, onPrefixSelect, prefixCountry, showModal, togglePrefixDropdown])

  const country = useMemo(() => {
    const effectiveCountryCode = localCountryCode ?? prefixCountry ?? ''
    const effectivePrefix = localPrefix ?? phonePrefix ?? ''
    return getPrefixCountry(effectiveCountryCode, effectivePrefix)
  }, [localCountryCode, localPrefix, phonePrefix, prefixCountry])

  const deferredTypehead = useDeferredValue(prefixTypeahead)
  const hiddenPrefixes = useMemo(() => {
    if (deferredTypehead) {
      const sanitizedPrefix = deferredTypehead.replace('+', '')
      const hiddenCountries = allowedCountries.filter(option =>
        !option.name.toLowerCase().includes(sanitizedPrefix.toLowerCase()) &&
        !option.dialCode.startsWith(sanitizedPrefix),
      )
      return new Set(hiddenCountries)
    }
    return new Set()
  }, [allowedCountries, deferredTypehead])

  const allowedCountriesSorted = useMemo(() => {
    return sortBy(allowedCountries, (country) => {
      const regionOrder = ISO2_START_ORDER.indexOf(country.iso2)
      if (regionOrder === -1) {
        // Put it at the bottom of the list if it's not in the desired order
        return 999
      }
      return regionOrder
    }, 'asc')
  }, [allowedCountries])

  const onPrefixDropdownClose = useCallback(() => {
    setPrefixTypeahead(undefined)
    closePrefixDropdown()
  }, [closePrefixDropdown])

  useEffect(() => {
    if (isPrefixDropdownOpen) {
      prefixSearchRef.current?.focus()
    }
  }, [isPrefixDropdownOpen])

  const onPhoneNumberChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onNumberChange(e)
    const currentNumber = e.currentTarget.value

    if (/^[0-9\s]*$/.test(currentNumber)) {
      setLocalNumber(currentNumber)
    } else {
      e.preventDefault()
    }

    if (useStrictValidation) {
      const inputEl = e.currentTarget
      const digitsOnly = inputEl.value.replace(/\s/g, '')
      import('./phoneValidationRules').then(({ validatePhoneNumber }) => {
        const validationError = validatePhoneNumber(digitsOnly, country.prefixCountry, allowLandline)

        switch (validationError) {
          case 'AU':
            inputEl.setCustomValidity(intl.formatMessage({
              id: 'CZrAtpc6W6JkQbEaaxsj',
              defaultMessage: 'Enter a valid phone number or change your region.',
              description: 'Validation error for invalid Australian phone number format',
            }))
            break
          case 'DEFAULT':
            inputEl.setCustomValidity(intl.formatMessage({
              id: '6zv3m99JPJZlxHihiF9n',
              defaultMessage: 'Please enter a valid phone number or change your region.',
              description: 'Validation error for phone number with invalid length',
            }))
            break
          default:
            inputEl.setCustomValidity('')
        }
      }).catch(() => {
        inputEl.setCustomValidity('')
      })
    } else if (
      country.prefixCountry === 'AU' && e.currentTarget.value.length > 1 &&
      (
        (e.currentTarget.value.length === 10 && (!e.currentTarget.value.startsWith('04') && !e.currentTarget.value.startsWith('05')) && (!allowLandline || !e.currentTarget.value.startsWith('0'))) ||
        (e.currentTarget.value.length === 9 && (!e.currentTarget.value.startsWith('4') && !e.currentTarget.value.startsWith('5')) && !allowLandline) ||
        (e.currentTarget.value.length !== 9 && e.currentTarget.value.length !== 10)
      )
    ) {
      e.currentTarget.setCustomValidity(intl.formatMessage({
        id: 'CZrAtpc6W6JkQbEaaxsj',
        defaultMessage: 'Enter a valid phone number or change your region.',
        description: 'Validation error for invalid Australian phone number format',
      }))
    } else {
      e.currentTarget.setCustomValidity('')
    }
  }, [setLocalNumber, country, onNumberChange, allowLandline, useStrictValidation, intl])

  const onHiddenCountryCodeChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    // while users can't type into this hidden field - auto complete can fill it in
    // so we get a change from auto-complete, make sure we handle it
    const dialCode = e.currentTarget.value.toLowerCase()
    const country = allowedCountries.find(country => country.dialCode === dialCode)
    if (country) {
      onPrefixSelect(country)
    }
  }, [allowedCountries, onPrefixSelect])

  return (<>
    <InputLabelWrap
      label={label}
      htmlFor={id}
      className={className}
      required={required}
      hidden={hidden}
    >
      <PhoneNumberInput
        startIcon={
          <PrefixDropdown
            ref={prefixTriggerRef}
            onClick={onPrefixToggle}
            disabled={disabled}
          >
            <HiddenInput
              type="text"
              disabled={disabled}
              name={prefixNameOverride ?? `${name}.prefix`}
              value={country.phonePrefix}
              autoComplete="tel-country-code"
              onChange={onHiddenCountryCodeChange}
              required={required}
            />
            {countryCodeNameOverride && <HiddenInput
              type="text"
              disabled={disabled}
              name={countryCodeNameOverride}
              value={country.prefixCountry}
              autoComplete="tel-country-name"
              onChange={() => {}}
              required={required}
            />}

            <Flag data-testid={`flag-${country?.prefixCountry}`} countryCode={country?.prefixCountry} />
            <InputText variant="text-regular">{intl.formatMessage({ id: 'skop--nNjmuxX5-gnfiG', defaultMessage: '+{prefix}', description: 'Phone country code prefix display in the phone input section' }, { prefix: country.phonePrefix })}</InputText>
            <LineAngleDownIcon size="XS" />
          </PrefixDropdown>
        }
        disabled={disabled}
        maxLength={15}
        minLength={7}
        id={id}
        type="tel"
        placeholder={placeholder}
        name={phoneNameOverride ?? `${name}.phone`}
        onChange={onPhoneNumberChange}
        onBlur={onBlur}
        onFocus={onFocus}
        value={phoneNumber ?? localNumber}
        ref={inputRef}
        required={required}
        requiredErrorMessage={requiredErrorMessage}
        data-testid={inputTestId}
        noValidationSpacing={noValidationSpacing}
        pattern="^[0-9\s]*$"
        autoComplete={autoComplete}
        helpText={helpText}
      />

    </InputLabelWrap>
    <DropdownList
      size="S"
      open={isPrefixDropdownOpen}
      onClose={onPrefixDropdownClose}
      anchorRef={prefixTriggerRef}
      triggerRef={prefixTriggerRef}
      placement="bottom-start"
      headerExtension={isPrefixDropdownOpen && <TextInput
        autoComplete="off"
        onChange={(e) => setPrefixTypeahead(e.currentTarget.value)}
        defaultValue={prefixTypeahead}
        endIcon={<LineSearchIcon />}
        placeholder={intl.formatMessage({
          id: 'hfmCIxxffZoosrifz8pw',
          defaultMessage: 'Search by country or code',
          description: 'Placeholder for phone prefix search input in the search interface',
        })}
        ref={prefixSearchRef}
        noValidationSpacing
      />}
    >
      {isPrefixDropdownOpen && <>
        {hiddenPrefixes.size === allowedCountries.length && <BodyText variant="medium">{intl.formatMessage({
          id: 'AKo6awDS665mn3JKE2ew',
          defaultMessage: 'No matching dial codes',
          description: 'Empty state text when no phone prefix matches the search',
        })}
        </BodyText>}
        {allowedCountriesSorted.map(country => <PhonePrefixItem
          size="medium"
          key={country.iso2}
          selected={localCountryCode === country.iso2.toLocaleUpperCase()}
          country={country}
          onSelect={onPrefixSelect}
          hidden={hiddenPrefixes.has(country)}
        />)}
      </>}
    </DropdownList>
  </>)
}

export default PhoneInput
